import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router";
import { Button } from "../../components/ui/Button";
import { CriteriaList } from "../../components/report/CriteriaList";
import { PassFailHero } from "../../components/report/PassFailHero";
import { ReportTopBar } from "../../components/report/ReportTopBar";
import { SummaryMd } from "../../components/report/SummaryMd";
import { TurnBreakdown, type TurnSummary } from "../../components/report/TurnBreakdown";
import { TurnRecap } from "../../components/report/TurnRecap";
import type { CriterionKey } from "../../lib/duo-rubric";
import type { Totals } from "../../lib/exam/scoring-types";
import type { SessionUser } from "../../lib/session-loader";
import { authedFetch } from "../../lib/api-client";
import type { ApiEnvelope } from "@a2/core/schemas";
import { useT } from "../../lib/use-t";

/** First non-empty line of the summary markdown, stripped of heading/quote marks. */
function summaryFirstLine(md: string): string {
  const line = md
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith("#"));
  if (!line) return "";
  return line.replace(/^[-*]\s+/, "").replace(/^["“]|["”]$/g, "");
}

type RecommendedTopic = {
  slug: string;
  title: string;
  summary: string;
  duration: string;
};

type ReportResponse = {
  sessionId: string;
  totals: Totals;
  weakest: CriterionKey | null;
  summaryMd: string;
  summarySource: "llm" | "template";
  turns: TurnSummary[];
  recommendedTopics: RecommendedTopic[];
};

type BackLink = { href: string; labelKey: string; disabled?: boolean };

function resolveBackLink(opts: {
  from: string | undefined;
}): BackLink | null {
  const { from } = opts;

  // Came from finishing an exam — there's nothing to navigate back to.
  // Render a disabled back affordance so the slot doesn't move when
  // the user reaches the rapport from any other origin.
  if (from === "exam") {
    return { href: "#", labelKey: "spreken.report.backLinks.examCompleted", disabled: true };
  }

  // Came from a recent-sessions row on the welcome page — return to that
  // section specifically (anchor-scroll), not just the top of /.
  if (from === "recent") {
    return { href: "/#recent-heading", labelKey: "spreken.report.backLinks.toRecentSessions" };
  }

  // Came from the full session list at /geschiedenis.
  if (from === "history") {
    return { href: "/geschiedenis", labelKey: "spreken.report.backLinks.toHistory" };
  }

  // Admin paths — the LXC resolved a user-specific label server-side
  // (Terug naar <name>) using getUserDisplayName. The SPA doesn't have
  // that lookup wired yet; we render a generic "Terug naar gebruiker"
  // rather than silently dropping the link. Follow-up: extend
  // /api/admin/users/:id to expose displayName so we can match the LXC.
  if (from === "admin-users") {
    return { href: "/admin", labelKey: "spreken.report.backLinks.toAdminUsers" };
  }
  if (from === "admin-user") {
    return { href: "/admin", labelKey: "spreken.report.backLinks.toAdminUser" };
  }

  // Default fallback: the welcome page is always a safe home.
  return { href: "/", labelKey: "spreken.report.backLinks.toHomepage" };
}

function ReportClient({
  sessionId,
  displayName,
}: {
  sessionId: string;
  displayName: string;
}) {
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();
  const t = useT();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/exam/report", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });
        const json = (await res.json()) as ApiEnvelope<ReportResponse>;
        if (cancelled) return;
        if (!json.ok) {
          setError(json.error.message);
          return;
        }
        setReport(json.data);
      } catch {
        if (!cancelled) setError(t("spreken.report.errorLoadFailed"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (error) {
    // Most common cause is the "no_turns" 409 — the student hit the report
    // route before answering anything (refreshed an empty session, or
    // navigated directly). Treat it as a calm empty state, not a failure.
    const isEmpty = /geen ingevulde beurten/i.test(error);
    return (
      <section
        role="alert"
        aria-live="polite"
        className="space-y-6"
      >
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge">
            {isEmpty ? t("spreken.report.emptyStateEyebrow") : t("spreken.report.errorEyebrow")}
          </p>
          <h2
            className="text-3xl sm:text-4xl font-semibold tracking-tight"
            style={{ color: isEmpty ? "var(--ink)" : "var(--fail)" }}
          >
            {isEmpty
              ? t("spreken.report.emptyStateTitle")
              : t("spreken.report.errorTitle")}
          </h2>
          <p className="font-serif-italic text-lg text-[var(--ink-muted)] max-w-prose">
            {isEmpty
              ? t("spreken.report.emptyStateDescription")
              : error}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => nav("/", { replace: true })} variant="primary">
            {isEmpty ? t("spreken.report.beginExerciseButton") : t("spreken.report.backToStartButton")}
          </Button>
          {!isEmpty ? (
            <Button onClick={() => window.location.reload()} variant="secondary">
              {t("spreken.report.retryButton")}
            </Button>
          ) : null}
        </div>
      </section>
    );
  }
  if (!report) {
    return (
      <div
        className="space-y-10"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={t("spreken.report.loadingAriaLabel")}
      >
        {/* Verdict placeholder — matches the new typographic hero. */}
        <div className="space-y-3" aria-hidden>
          <div className="h-16 sm:h-20 w-72 max-w-full rounded skeleton-shimmer" />
          <div className="h-7 w-64 max-w-full rounded skeleton-shimmer" />
        </div>
        {/* Five criterion rows. */}
        <div className="space-y-2.5" aria-hidden>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-4 w-full rounded skeleton-shimmer" />
          ))}
        </div>
        {/* Summary prose preview. */}
        <div className="space-y-2" aria-hidden>
          <div className="h-5 w-40 rounded skeleton-shimmer" />
          <div className="h-4 w-full rounded skeleton-shimmer" />
          <div className="h-4 w-11/12 rounded skeleton-shimmer" />
          <div className="h-4 w-4/5 rounded skeleton-shimmer" />
        </div>
      </div>
    );
  }

  const hasRecommended = report.recommendedTopics.length > 0;
  const praise = displayName
    ? t("spreken.report.heroPraiseFmt", { name: displayName })
    : t("spreken.report.heroPraisePlain");
  const statusLabel = report.totals.passed
    ? t("spreken.report.statusPassed")
    : t("spreken.report.statusFailed");

  return (
    <div className="space-y-8">
      <PassFailHero
        totals={report.totals}
        praise={praise}
        statusLabel={statusLabel}
        summaryLine={summaryFirstLine(report.summaryMd)}
      />

      <CriteriaList
        totals={report.totals}
        weakest={report.weakest}
        title={t("spreken.report.criteriaCardTitle")}
      />

      <TurnRecap turns={report.turns} title={t("spreken.report.perTurnCardTitle")} />

      <SummaryMd md={report.summaryMd} source={report.summarySource} />

      {/* Recommended-topics is next-step content; sits below the recap so
          the reading order is verdict → criteria → per-beurt → toelichting
          → what to do next → optional drill-down. */}
      {hasRecommended ? (
        <section
          aria-labelledby="report-topics"
          className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 sm:p-6 shadow-[var(--shadow)] space-y-4"
        >
          <header className="space-y-1">
            <h3 id="report-topics" className="text-lg sm:text-xl font-semibold tracking-tight">
              {t("spreken.report.recommendedTopicsTitle")}
            </h3>
            <p className="text-sm text-[var(--ink-muted)] font-serif-italic">
              {t("spreken.report.recommendedTopicsDescription")}
            </p>
          </header>
          <ul className="space-y-2">
            {report.recommendedTopics.map((t) => (
              <li key={t.slug}>
                <Link
                  to={`/leren/${t.slug}`}
                  className="group block rounded-lg border border-[var(--border)] bg-[var(--panel-soft)] hover:bg-[var(--panel)] hover:border-[var(--accent)]/40 transition-colors px-4 py-3"
                >
                  <div className="flex items-baseline justify-between gap-3 mb-0.5">
                    <span className="font-medium">{t.title}</span>
                    <span className="font-mono-badge text-xs text-[var(--ink-faint)] shrink-0 tabular-nums">
                      {t.duration}
                    </span>
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <p className="text-sm text-[var(--ink-muted)] font-serif-italic flex-1">
                      {t.summary}
                    </p>
                    <span
                      aria-hidden
                      className="text-[var(--ink-faint)] opacity-40 group-hover:opacity-100 group-hover:text-[var(--accent)] transition-all group-hover:translate-x-0.5 shrink-0"
                    >
                      →
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <TurnBreakdown turns={report.turns} />

      {/* Action row — warm "oefen opnieuw" advances the loop; secondary
          returns to the dashboard. The session is already persisted on
          finalize, so "Bewaard in Voortgang" is a passive confirmation
          that links to the progress page. Download stays a power link. */}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button onClick={() => nav("/oefenen?module=spreken")} variant="warm">
          {t("spreken.report.actionPracticeAgain")}
        </Button>
        <Button onClick={() => nav("/vandaag")} variant="secondary">
          {t("spreken.report.actionBackToToday")}
        </Button>
        <Link
          to="/voortgang"
          className="inline-flex items-center gap-1.5 rounded-[var(--radius)] px-3 py-2 text-sm text-[var(--accent)] hover:bg-[var(--accent-soft)] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
        >
          <span aria-hidden>✓</span>
          {t("spreken.report.savedToProgress")}
        </Link>
        <button
          type="button"
          className="ml-auto text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors underline-offset-4 hover:underline"
          onClick={() => {
            const blob = new Blob([report.summaryMd], { type: "text/markdown" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `a2-easy-rapport-${sessionId}.md`;
            a.click();
            URL.revokeObjectURL(a.href);
          }}
        >
          {t("spreken.report.downloadMarkdown")}
        </button>
      </div>
    </div>
  );
}

export function ReportPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [params] = useSearchParams();
  const session = useOutletContext<SessionUser>();
  const t = useT();
  const from = params.get("from") ?? undefined;
  const back = resolveBackLink({ from });

  // The result surface always needs an exit. When the back target is the
  // disabled "exam completed" affordance (came straight from the runner),
  // close to the dashboard instead of leaving the X inert.
  const closeTarget =
    back && !back.disabled ? { href: back.href } : { href: "/vandaag" };

  if (!sessionId) {
    return (
      <div className="flex min-h-dvh flex-col bg-[var(--bg)]">
        <ReportTopBar
          back={closeTarget}
          label={t("spreken.report.resultatePill")}
          closeAriaLabel={t("spreken.report.closeAriaLabel")}
        />
        <main className="mx-auto w-full max-w-[760px] px-4 py-8 sm:px-6">
          <p className="text-sm text-[var(--fail)]" role="alert">
            {t("spreken.report.sessionMissing")}
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--bg)]">
      <ReportTopBar
        back={closeTarget}
        label={t("spreken.report.resultatePill")}
        closeAriaLabel={t("spreken.report.closeAriaLabel")}
      />
      <main className="mx-auto w-full max-w-[760px] px-4 py-8 pb-12 sm:px-6 sm:py-10">
        <ReportClient sessionId={sessionId} displayName={session?.displayName ?? ""} />
      </main>
    </div>
  );
}
